<template>
  <div v-if="project">
    <div class="page-header">
      <div>
        <div class="breadcrumb-sm">
          <span class="breadcrumb-link" @click="$router.back()">Projects</span>
          <span class="breadcrumb-sep">›</span>
          <span>{{ project.project_code }}</span>
        </div>
        <h5 class="page-title">{{ project.project_name }}</h5>
        <p class="page-sub">{{ project.type?.replace(/_/g, ' ') }} · {{ project.billing_method?.replace(/_/g, ' ') }}</p>
      </div>
      <div class="header-actions">
        <button v-if="project.status === 'draft'" class="btn-primary" @click="activateProject">
          Activate Project
        </button>
        <button v-if="project.status === 'active'" class="btn-outline btn-outline--danger" @click="closeModalOpen = true">
          Close Project
        </button>
      </div>
    </div>

    <!-- Summary cards -->
    <div class="summary-grid">
      <div class="summary-card">
        <div class="summary-label">Contract Value</div>
        <div class="summary-value">{{ project.contract_value ? formatMoney(project.contract_value) : '—' }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Approved Budget</div>
        <div class="summary-value">{{ approvedBudgetTotal ? formatMoney(approvedBudgetTotal) : '—' }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Actual Cost</div>
        <div class="summary-value">{{ formatMoney(totalCost) }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Status</div>
        <div class="summary-value"><span :class="['status-badge', `status-badge--${project.status}`]">{{ project.status }}</span></div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tab-bar">
      <button v-for="t in tabs" :key="t.key" :class="['tab-btn', { active: activeTab === t.key }]" @click="activeTab = t.key">
        {{ t.label }}
      </button>
    </div>

    <!-- Overview -->
    <div v-if="activeTab === 'overview'" class="card">
      <div class="detail-grid">
        <div class="detail-item"><span class="detail-label">Project Code</span><span class="detail-value mono">{{ project.project_code }}</span></div>
        <div class="detail-item"><span class="detail-label">Manager</span><span class="detail-value">{{ project.manager ? project.manager.first_name + ' ' + project.manager.last_name : '—' }}</span></div>
        <div class="detail-item"><span class="detail-label">Customer</span><span class="detail-value">{{ project.customer?.customer_name || '—' }}</span></div>
        <div class="detail-item"><span class="detail-label">Currency</span><span class="detail-value">{{ project.currency || '—' }}</span></div>
        <div class="detail-item"><span class="detail-label">Planned Start</span><span class="detail-value">{{ formatDate(project.start_date) }}</span></div>
        <div class="detail-item"><span class="detail-label">Planned End</span><span class="detail-value">{{ formatDate(project.end_date) }}</span></div>
        <div class="detail-item"><span class="detail-label">Actual Start</span><span class="detail-value">{{ formatDate(project.actual_start_date) }}</span></div>
        <div class="detail-item"><span class="detail-label">Actual End</span><span class="detail-value">{{ formatDate(project.actual_end_date) }}</span></div>
        <div class="detail-item"><span class="detail-label">Not-to-Exceed</span><span class="detail-value">{{ project.not_to_exceed ? formatMoney(project.not_to_exceed) : '—' }}</span></div>
        <div class="detail-item"><span class="detail-label">Revenue Method</span><span class="detail-value">{{ project.revenue_method?.replace(/_/g, ' ') || '—' }}</span></div>
      </div>
      <div v-if="project.description" class="mt-16">
        <div class="detail-label">Description</div>
        <p class="mt-4">{{ project.description }}</p>
      </div>
    </div>

    <!-- WBS -->
    <div v-if="activeTab === 'wbs'" class="card">
      <div class="card-header-row">
        <span class="card-section-title">Work Breakdown Structure</span>
        <button class="btn-sm-primary" @click="openAddWbs(null)">+ Add Element</button>
      </div>
      <div v-if="!wbs.length" class="empty-state-sm">No WBS elements yet.</div>
      <div v-else class="wbs-tree">
        <wbs-node v-for="el in wbs" :key="el.id" :node="el" @add-child="openAddWbs" @edit="openEditWbs" @delete="deleteWbs" />
      </div>
    </div>

    <!-- Team -->
    <div v-if="activeTab === 'team'" class="card">
      <div class="card-header-row">
        <span class="card-section-title">Project Team</span>
        <button class="btn-sm-primary" @click="openAddMember">+ Add Member</button>
      </div>
      <div v-if="!members.length" class="empty-state-sm">No team members yet.</div>
      <table v-else class="data-table">
        <thead><tr><th>Name</th><th>Role</th><th>Start</th><th>End</th><th></th></tr></thead>
        <tbody>
          <tr v-for="m in members" :key="m.id">
            <td>{{ m.employee ? m.employee.first_name + ' ' + m.employee.last_name : '—' }}</td>
            <td>{{ m.role || '—' }}</td>
            <td>{{ formatDate(m.start_date) }}</td>
            <td>{{ m.end_date ? formatDate(m.end_date) : 'Ongoing' }}</td>
            <td><button class="btn-link btn-link--danger" @click="confirmRemoveMember(m.id)">Remove</button></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Budgets -->
    <div v-if="activeTab === 'budget'" class="card">
      <div class="card-header-row">
        <span class="card-section-title">Budgets</span>
        <button class="btn-sm-primary" @click="openNewBudget">+ New Budget</button>
      </div>
      <div v-if="!budgets.length" class="empty-state-sm">No budgets created yet.</div>
      <table v-else class="data-table">
        <thead><tr><th>Version</th><th class="amount-col">Total Amount</th><th>Status</th><th>Approved At</th><th></th></tr></thead>
        <tbody>
          <tr v-for="b in budgets" :key="b.id" :class="{ 'row-selected': activeBudget && activeBudget.id === b.id }">
            <td><span class="mono">{{ b.version }}</span></td>
            <td class="amount-col">{{ formatMoney(b.total_amount) }}</td>
            <td><span :class="['status-badge', `status-badge--bud-${b.status}`]">{{ budgetStatusLabel(b.status) }}</span></td>
            <td>{{ b.approved_at ? formatDate(b.approved_at) : '—' }}</td>
            <td>
              <button class="btn-link" @click="loadBudgetDetail(b.id)">
                {{ activeBudget && activeBudget.id === b.id ? 'Hide' : 'View' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- Budget detail panel -->
      <div v-if="activeBudget" class="budget-detail">
        <div class="budget-detail-header">
          <span class="budget-detail-title">
            Budget ({{ activeBudget.version }})
            <span :class="['status-badge', `status-badge--bud-${activeBudget.status}`]">{{ budgetStatusLabel(activeBudget.status) }}</span>
          </span>
          <div class="billing-actions">
            <button v-if="activeBudget.status === 'draft'" class="btn-link" @click="submitBudget(activeBudget.id)">Submit</button>
            <button v-if="activeBudget.status === 'pending_approval'" class="btn-link btn-link--success" @click="approveBudget(activeBudget.id)">Approve</button>
            <button v-if="activeBudget.status === 'pending_approval'" class="btn-link btn-link--danger" @click="rejectBudget(activeBudget.id)">Reject</button>
          </div>
        </div>
        <div v-if="activeBudget.notes" class="budget-notes">{{ activeBudget.notes }}</div>
        <div v-if="!activeBudget.lines || !activeBudget.lines.length" class="empty-state-sm">No line items.</div>
        <table v-else class="data-table">
          <thead><tr><th>WBS Element</th><th>Category</th><th class="amount-col">Hours</th><th class="amount-col">Amount</th><th>Notes</th></tr></thead>
          <tbody>
            <tr v-for="line in activeBudget.lines" :key="line.id">
              <td>{{ line.wbs_element ? `${line.wbs_element.code} – ${line.wbs_element.name}` : '—' }}</td>
              <td><span :class="['status-badge', `status-badge--cat-${line.cost_category}`]">{{ line.cost_category }}</span></td>
              <td class="amount-col">{{ line.hours ?? '—' }}</td>
              <td class="amount-col">{{ formatMoney(line.amount) }}</td>
              <td class="notes-col">{{ line.notes ?? '—' }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td colspan="3">Total</td>
              <td class="amount-col">{{ formatMoney(activeBudget.total_amount) }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- New Budget Modal -->
    <div :class="['modal-overlay', { open: addBudgetOpen }]" @click.self="addBudgetOpen = false">
      <div class="modal-box modal-box--lg">
        <div class="modal-header">
          <span class="modal-title">New Budget</span>
          <button class="modal-close" @click="addBudgetOpen = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Notes</label>
            <textarea class="form-control" v-model="budgetForm.notes" rows="2" placeholder="Optional notes about this budget version"></textarea>
          </div>
          <div class="budget-lines-header">
            <span style="font-size:13px;font-weight:600;color:#1a2b45;">Line Items</span>
            <button type="button" class="btn-link" @click="addBudgetLine">+ Add Line</button>
          </div>
          <div v-if="!budgetLines.length" class="empty-state-sm" style="padding:12px 0;">No line items added. Click "+ Add Line" to begin.</div>
          <table v-else class="data-table budget-lines-table">
            <thead><tr><th>WBS Element</th><th>Category</th><th>Amount</th><th>Hours</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(line, i) in budgetLines" :key="i">
                <td>
                  <Multiselect v-model="line.wbs_id" :options="wbsOptions" valueProp="value" label="name" :searchable="true" :can-clear="true" placeholder="Select WBS…" class="custom-multiselect budget-line-ms" />
                </td>
                <td>
                  <select class="form-control" v-model="line.cost_category">
                    <option value="labour">Labour</option>
                    <option value="materials">Materials</option>
                    <option value="subcontract">Subcontract</option>
                    <option value="expenses">Expenses</option>
                    <option value="overhead">Overhead</option>
                  </select>
                </td>
                <td><input type="number" class="form-control" v-model="line.amount" placeholder="0.00" min="0" step="0.01" /></td>
                <td><input type="number" class="form-control" v-model="line.hours" placeholder="0" min="0" /></td>
                <td><input type="text" class="form-control" v-model="line.notes" placeholder="Notes" /></td>
                <td><button type="button" class="btn-link btn-link--danger" @click="removeBudgetLine(i)">✕</button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="modal-footer">
          <span class="budget-total-preview">Total: {{ formatMoney(budgetLinesTotalAmt) }}</span>
          <button class="btn-outline" @click="addBudgetOpen = false">Cancel</button>
          <button class="btn-primary" @click="saveBudget" :disabled="saving">{{ saving ? 'Saving…' : 'Create Budget' }}</button>
        </div>
      </div>
    </div>

    <!-- Timesheets -->
    <div v-if="activeTab === 'timesheets'" class="card">
      <div class="card-header-row">
        <span class="card-section-title">Time Entries</span>
        <button class="btn-sm-primary" @click="openLogTime">+ Log Time</button>
      </div>
      <div v-if="!timeEntries.length" class="empty-state-sm">No time entries logged yet.</div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Employee</th>
            <th>WBS</th>
            <th>Date</th>
            <th>Hours</th>
            <th>Description</th>
            <th>Billable</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in timeEntries" :key="e.id">
            <td>{{ e.timesheet?.employee ? e.timesheet.employee.first_name + ' ' + e.timesheet.employee.last_name : '—' }}</td>
            <td><span class="mono">{{ e.wbs_element?.code }}</span> <span class="text-dim">{{ e.wbs_element?.name }}</span></td>
            <td>{{ formatDate(e.work_date) }}</td>
            <td>{{ e.hours }}h</td>
            <td>{{ e.description || '—' }}</td>
            <td><span :class="['status-badge', e.billable ? 'status-badge--active' : 'status-badge--draft']">{{ e.billable ? 'Billable' : 'Non-billable' }}</span></td>
            <td><span :class="['status-badge', `status-badge--${e.timesheet?.status ?? 'draft'}`]">{{ e.timesheet?.status ?? 'draft' }}</span></td>
            <td>
              <button v-if="['draft','rejected'].includes(e.timesheet?.status)" class="btn-link btn-link--danger" @click="deleteTimeEntry(e.id)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Revenue Recognition -->
    <div v-if="activeTab === 'revenue'" class="card">
      <div class="card-header-row">
        <div>
          <span class="card-section-title">Revenue Recognition</span>
          <span class="method-badge">Method: {{ project.revenue_method?.replace(/_/g, ' ') || 'cost to cost' }}</span>
        </div>
        <button class="btn-sm-primary" @click="runRevenueOpen = true">Run Recognition</button>
      </div>
      <div v-if="!revenue.length" class="empty-state-sm">No revenue recognition entries yet. Run recognition for a period to begin.</div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Period</th>
            <th>Method</th>
            <th class="amount-col">Recognised</th>
            <th class="amount-col">WIP</th>
            <th class="amount-col">Deferred</th>
            <th>Journal</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in revenue" :key="r.id">
            <td class="mono">{{ r.period }}</td>
            <td>{{ r.method?.replace(/_/g, ' ') }}</td>
            <td class="amount-col rev-positive">{{ formatMoney(r.recognised_amt) }}</td>
            <td class="amount-col">{{ formatMoney(r.wip_amt) }}</td>
            <td class="amount-col">{{ formatMoney(r.deferred_amt) }}</td>
            <td><span v-if="r.gl_journal_id" class="mono text-dim">{{ r.gl_journal_id.slice(0, 8) }}…</span><span v-else class="text-dim">—</span></td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="total-row">
            <td colspan="2"><strong>Total</strong></td>
            <td class="amount-col"><strong>{{ formatMoney(revenue.reduce((s,r) => s + +r.recognised_amt, 0)) }}</strong></td>
            <td class="amount-col">{{ formatMoney(revenue.reduce((s,r) => s + +r.wip_amt, 0)) }}</td>
            <td class="amount-col">{{ formatMoney(revenue.reduce((s,r) => s + +r.deferred_amt, 0)) }}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Run Revenue Recognition Modal -->
    <div class="modal-overlay" :class="{ open: runRevenueOpen }" @click.self="runRevenueOpen = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <span class="modal-title">Run Revenue Recognition</span>
          <button class="modal-close" @click="runRevenueOpen = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="rev-info-box">
            <strong>Method:</strong> {{ project?.revenue_method?.replace(/_/g, ' ') || 'cost to cost' }}<br>
            <span class="text-dim rev-info-desc">
              <span v-if="project?.revenue_method === 'on_billing'">Recognised amount = sum of invoiced billing events whose due date falls within the period.</span>
              <span v-else>Recognised amount = (actual costs to date ÷ approved budget) × contract value.</span>
            </span>
          </div>
          <div class="form-group" style="margin-top:16px;">
            <label>Period <span class="req">*</span></label>
            <input type="month" v-model="revenueForm.period" class="form-control" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="runRevenueOpen = false">Cancel</button>
          <button class="btn-primary" @click="runRecognition" :disabled="saving">{{ saving ? 'Running…' : 'Run Recognition' }}</button>
        </div>
      </div>
    </div>

    <!-- Billing -->
    <div v-if="activeTab === 'billing'" class="card">
      <div class="card-header-row">
        <span class="card-section-title">Billing Events</span>
        <button class="btn-sm-primary" @click="openAddBilling">+ Add Billing Event</button>
      </div>
      <div v-if="!billing.length" class="empty-state-sm">No billing events yet.</div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Type</th><th>WBS</th><th>Amount</th><th>Due Date</th><th>Status</th><th>Invoice Ref</th><th>Notes</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="b in billing" :key="b.id">
            <td><span class="type-badge">{{ billingTypeLabel(b.type) }}</span></td>
            <td><span v-if="b.wbs_element" class="mono">{{ b.wbs_element.code }}</span><span v-else class="text-dim">—</span></td>
            <td class="amount-col">{{ formatMoney(b.amount) }}</td>
            <td>{{ b.due_date ? formatDate(b.due_date) : '—' }}</td>
            <td><span :class="['status-badge', `status-badge--bill-${b.status}`]">{{ billingStatusLabel(b.status) }}</span></td>
            <td>{{ b.invoice_id || '—' }}</td>
            <td class="notes-col">{{ b.notes || '—' }}</td>
            <td>
              <div class="billing-actions">
                <button v-if="b.status === 'draft'" class="btn-link" @click="submitBilling(b.id)">Submit</button>
                <button v-if="b.status === 'pending_approval'" class="btn-link btn-link--success" @click="approveBilling(b.id)">Approve</button>
                <button v-if="b.status === 'pending_approval'" class="btn-link btn-link--danger" @click="rejectBilling(b.id)">Reject</button>
                <button v-if="b.status === 'approved'" class="btn-link" @click="markBillingInvoiced(b.id)">Invoice</button>
                <button v-if="['draft','rejected'].includes(b.status)" class="btn-link btn-link--danger" @click="deleteBilling(b.id)">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add Billing Event Modal -->
    <div class="modal-overlay" :class="{ open: addBillingOpen }" @click.self="addBillingOpen = false">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">Add Billing Event</span>
          <button class="modal-close" @click="addBillingOpen = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>Type</label>
              <select v-model="billingForm.type" class="form-control">
                <option value="milestone">Milestone</option>
                <option value="fixed_fee">Fixed Fee</option>
                <option value="retainer">Retainer</option>
                <option value="time_and_material">Time &amp; Material</option>
              </select>
            </div>
            <div class="form-group">
              <label>Amount <span class="req">*</span></label>
              <input type="number" step="0.01" min="0" v-model="billingForm.amount" class="form-control" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>WBS Element</label>
            <Multiselect v-model="billingForm.wbs_id" label="name" :options="wbsOptions" :searchable="true" :close-on-select="true" :can-clear="true" placeholder="Optional — select WBS element..." class="custom-multiselect" />
          </div>
          <div class="form-group">
            <label>Due Date</label>
            <input type="date" v-model="billingForm.due_date" class="form-control" />
          </div>
          <div class="form-group">
            <label>Notes</label>
            <input v-model="billingForm.notes" class="form-control" placeholder="Description of billing event..." />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="addBillingOpen = false">Cancel</button>
          <button class="btn-primary" @click="saveBilling" :disabled="saving">{{ saving ? 'Saving...' : 'Create' }}</button>
        </div>
      </div>
    </div>

    <!-- Costs -->
    <div v-if="activeTab === 'costs'" class="card">
      <div class="card-header-row">
        <span class="card-section-title">Cost Entries</span>
        <button class="btn-sm-primary" @click="openAddCost">+ Add Cost</button>
      </div>
      <div v-if="!costs.length" class="empty-state-sm">No cost entries yet.</div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Category</th>
            <th>Source</th>
            <th>WBS</th>
            <th>Ref</th>
            <th>Amount</th>
            <th>Type</th>
            <th>Notes</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in costs" :key="c.id">
            <td>{{ formatDate(c.date) }}</td>
            <td><span :class="['status-badge', `status-badge--cat-${c.cost_category}`]">{{ c.cost_category || '—' }}</span></td>
            <td>{{ sourceLabel(c.source) }}</td>
            <td><span v-if="c.wbs_element" class="mono">{{ c.wbs_element.code }}</span><span v-else>—</span></td>
            <td>{{ c.source_ref || '—' }}</td>
            <td class="amount-col">{{ formatMoney(c.amount) }}</td>
            <td><span :class="['status-badge', c.commitment ? 'status-badge--pending' : 'status-badge--approved']">{{ c.commitment ? 'Committed' : 'Actual' }}</span></td>
            <td class="notes-col">{{ c.notes || '—' }}</td>
            <td><button class="btn-link btn-link--danger" @click="deleteCost(c.id)">Delete</button></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add Cost Modal -->
    <div class="modal-overlay" :class="{ open: addCostOpen }" @click.self="addCostOpen = false">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">Add Cost Entry</span>
          <button class="modal-close" @click="addCostOpen = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>Date <span class="req">*</span></label>
              <input type="date" v-model="costForm.date" class="form-control" />
            </div>
            <div class="form-group">
              <label>Amount <span class="req">*</span></label>
              <input type="number" step="0.01" min="0" v-model="costForm.amount" class="form-control" placeholder="0.00" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Source</label>
              <select v-model="costForm.source" class="form-control">
                <option value="expense">Expense</option>
                <option value="purchase_order">Purchase Order</option>
                <option value="ap_invoice">AP Invoice</option>
                <option value="inventory">Inventory</option>
                <option value="time">Time (Manual)</option>
              </select>
            </div>
            <div class="form-group">
              <label>Category</label>
              <select v-model="costForm.cost_category" class="form-control">
                <option value="labour">Labour</option>
                <option value="materials">Materials</option>
                <option value="subcontract">Subcontract</option>
                <option value="expenses">Expenses</option>
                <option value="overhead">Overhead</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>WBS Element</label>
            <Multiselect v-model="costForm.wbs_id" label="name" :options="wbsOptions" :searchable="true" :close-on-select="true" :can-clear="true" placeholder="Optional — select WBS element..." class="custom-multiselect" />
          </div>
          <div class="form-group">
            <label>Source Reference</label>
            <input v-model="costForm.source_ref" class="form-control" placeholder="Invoice no., PO no., etc." />
          </div>
          <div class="form-group">
            <label>Notes</label>
            <input v-model="costForm.notes" class="form-control" placeholder="Description of cost..." />
          </div>
          <div class="form-group--check">
            <label class="checkbox-label">
              <input type="checkbox" v-model="costForm.commitment" /> Committed (not yet incurred)
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="addCostOpen = false">Cancel</button>
          <button class="btn-primary" @click="saveCost" :disabled="saving">{{ saving ? 'Saving...' : 'Add Cost' }}</button>
        </div>
      </div>
    </div>

    <!-- Log Time Modal -->
    <div class="modal-overlay" :class="{ open: logTimeOpen }" @click.self="logTimeOpen = false">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">Log Time</span>
          <button class="modal-close" @click="logTimeOpen = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Employee <span class="req">*</span></label>
            <Multiselect v-model="logTimeForm.employee_id" label="name" :options="memberEmployeeOptions" :searchable="true" :close-on-select="true" placeholder="Select team member..." class="custom-multiselect" />
          </div>
          <div class="form-group">
            <label>WBS Element <span class="req">*</span></label>
            <Multiselect v-model="logTimeForm.wbs_id" label="name" :options="wbsOptions" :searchable="true" :close-on-select="true" placeholder="Select WBS element..." class="custom-multiselect" />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Work Date <span class="req">*</span></label>
              <input type="date" v-model="logTimeForm.work_date" class="form-control" />
            </div>
            <div class="form-group">
              <label>Hours <span class="req">*</span></label>
              <input type="number" step="0.25" min="0.25" max="24" v-model="logTimeForm.hours" class="form-control" placeholder="e.g. 3.5" />
            </div>
          </div>
          <div class="form-group">
            <label>Description</label>
            <input v-model="logTimeForm.description" class="form-control" placeholder="What did you work on?" />
          </div>
          <div class="form-group--check">
            <label class="checkbox-label">
              <input type="checkbox" v-model="logTimeForm.billable" /> Billable
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="logTimeOpen = false">Cancel</button>
          <button class="btn-primary" @click="saveTimeEntry" :disabled="saving">{{ saving ? 'Saving...' : 'Log Time' }}</button>
        </div>
      </div>
    </div>

    <!-- WBS Add/Edit Modal -->
    <div class="modal-overlay" :class="{ open: wbsModalOpen }" @click.self="wbsModalOpen = false">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">{{ wbsEditing ? 'Edit WBS Element' : 'Add WBS Element' }}</span>
          <button class="modal-close" @click="wbsModalOpen = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>Code <span class="req">*</span></label>
              <input v-model="wbsForm.code" class="form-control" placeholder="e.g. 1.1" />
            </div>
            <div class="form-group">
              <label>Type <span class="req">*</span></label>
              <select v-model="wbsForm.type" class="form-control">
                <option value="phase">Phase</option>
                <option value="task">Task</option>
                <option value="milestone">Milestone</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>Name <span class="req">*</span></label>
            <input v-model="wbsForm.name" class="form-control" placeholder="Element name" />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Planned Start</label>
              <input type="date" v-model="wbsForm.planned_start" class="form-control" />
            </div>
            <div class="form-group">
              <label>Planned End</label>
              <input type="date" v-model="wbsForm.planned_end" class="form-control" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Planned Hours</label>
              <input type="number" v-model="wbsForm.planned_hours" class="form-control" placeholder="0" min="0" />
            </div>
            <div class="form-group form-group--check">
              <label class="checkbox-label">
                <input type="checkbox" v-model="wbsForm.billable" />
                Billable
              </label>
            </div>
          </div>
          <div v-if="wbsForm.type === 'milestone'" class="form-row">
            <div class="form-group">
              <label>Milestone Amount</label>
              <input type="number" v-model="wbsForm.milestone_amount" class="form-control" placeholder="0.00" />
            </div>
            <div class="form-group">
              <label>Milestone Due Date</label>
              <input type="date" v-model="wbsForm.milestone_due_date" class="form-control" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="wbsModalOpen = false">Cancel</button>
          <button class="btn-primary" @click="saveWbs" :disabled="saving">{{ saving ? 'Saving...' : wbsEditing ? 'Update' : 'Add Element' }}</button>
        </div>
      </div>
    </div>

    <!-- Add Member Modal -->
    <div class="modal-overlay" :class="{ open: addMemberOpen }" @click.self="addMemberOpen = false">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">Add Team Member</span>
          <button class="modal-close" @click="addMemberOpen = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Employee <span class="req">*</span></label>
            <Multiselect
              v-model="memberForm.employee_id"
              label="name"
              :options="employeeOptions"
              :searchable="true"
              :close-on-select="true"
              :create-option="false"
              placeholder="Search employee..."
              class="custom-multiselect"
            />
          </div>
          <div class="form-group">
            <label>Role</label>
            <input v-model="memberForm.role" class="form-control" placeholder="e.g. Developer, Designer..." />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Start Date</label>
              <input type="date" v-model="memberForm.start_date" class="form-control" />
            </div>
            <div class="form-group">
              <label>End Date</label>
              <input type="date" v-model="memberForm.end_date" class="form-control" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="addMemberOpen = false">Cancel</button>
          <button class="btn-primary" @click="saveMember" :disabled="saving">{{ saving ? 'Saving...' : 'Add Member' }}</button>
        </div>
      </div>
    </div>

    <!-- Generic Confirm Modal -->
    <div :class="['modal-overlay', { open: confirmModal.open }]" @click.self="confirmModal.open = false">
      <div class="modal-box modal-box--sm confirm-modal">
        <div class="confirm-icon-wrap">
          <span class="confirm-icon">⚠</span>
        </div>
        <div class="confirm-body">
          <p class="confirm-title">{{ confirmModal.title }}</p>
          <p class="confirm-desc">{{ confirmModal.desc }}</p>
        </div>
        <div class="confirm-footer">
          <button class="btn-outline" @click="confirmModal.open = false">Cancel</button>
          <button class="btn-danger" @click="doConfirm">{{ confirmModal.btnLabel }}</button>
        </div>
      </div>
    </div>

    <!-- Close project modal -->
    <div class="modal-overlay" :class="{ open: closeModalOpen }" @click.self="closeModalOpen = false">
      <div class="modal-box">
        <div class="modal-header"><span class="modal-title">Close Project</span><button class="modal-close" @click="closeModalOpen = false">&times;</button></div>
        <div class="modal-body">
          <div class="form-group">
            <label>Actual End Date</label>
            <input type="date" v-model="closeForm.actual_end_date" class="form-control" />
          </div>
          <div class="form-group">
            <label>Closure Notes</label>
            <textarea v-model="closeForm.notes" class="form-control" rows="3"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="closeModalOpen = false">Cancel</button>
          <button class="btn-primary btn--danger" @click="confirmClose" :disabled="saving">{{ saving ? 'Closing...' : 'Close Project' }}</button>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="loading-state"><div class="spinner"></div><p>Loading project...</p></div>
</template>

<script>
import axios from 'axios';
import { h } from 'vue';
import { useToast } from 'vue-toastification';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

const WbsNode = {
  name: 'WbsNode',
  props: ['node'],
  emits: ['add-child', 'edit', 'delete'],
  render() {
    const node = this.node;
    const emit = (ev, arg) => this.$emit(ev, arg);
    return h('div', { class: 'wbs-item' }, [
      h('div', { class: 'wbs-row' }, [
        h('span', { class: 'wbs-code' }, node.code),
        h('span', { class: 'wbs-name' }, node.name),
        h('span', { class: ['status-badge', `status-badge--${node.type}`] }, node.type),
        h('div', { class: 'wbs-actions' }, [
          h('button', { class: 'btn-icon', title: 'Add child', onClick: () => emit('add-child', node.id) }, '+'),
          h('button', { class: 'btn-icon', title: 'Edit', onClick: () => emit('edit', node) }, '✎'),
          h('button', { class: 'btn-icon btn-icon--danger', title: 'Delete', onClick: () => emit('delete', node.id) }, '✕'),
        ]),
      ]),
      (node.children && node.children.length)
        ? h('div', { class: 'wbs-children' },
            node.children.map(c => h(WbsNode, {
              key: c.id,
              node: c,
              onAddChild: id => emit('add-child', id),
              onEdit:     n  => emit('edit', n),
              onDelete:   id => emit('delete', id),
            }))
          )
        : null,
    ]);
  },
};

export default {
  name: 'ProjectDetail',
  components: { WbsNode, Multiselect },
  setup() { return { toast: useToast() }; },
  data() {
    return {
      project: null, wbs: [], members: [], budgets: [], employees: [], timeEntries: [], costs: [], billing: [], revenue: [],
      activeTab: 'overview', saving: false,
      closeModalOpen: false, addMemberOpen: false, logTimeOpen: false, addCostOpen: false, addBillingOpen: false, runRevenueOpen: false, addBudgetOpen: false,
      confirmModal: { open: false, title: '', desc: '', btnLabel: 'Delete' },
      activeBudget: null,
      budgetForm: { notes: '' },
      budgetLines: [],
      closeForm: { actual_end_date: '', notes: '' },
      memberForm: { employee_id: '', role: '', start_date: '', end_date: '' },
      logTimeForm: { employee_id: '', wbs_id: '', work_date: '', hours: '', description: '', billable: true },
      costForm: { wbs_id: '', source: 'expense', source_ref: '', cost_category: 'expenses', amount: '', date: '', commitment: false, notes: '' },
      billingForm: { wbs_id: '', type: 'milestone', amount: '', due_date: '', notes: '' },
      revenueForm: { period: new Date().toISOString().slice(0, 7) },
      wbsModalOpen: false, wbsEditing: null, wbsParentId: null,
      wbsForm: { code: '', name: '', type: 'task', planned_start: '', planned_end: '', planned_hours: '', billable: true, milestone_amount: '', milestone_due_date: '' },
      tabs: [
        { key: 'overview',    label: 'Overview' },
        { key: 'wbs',         label: 'WBS' },
        { key: 'team',        label: 'Team' },
        { key: 'budget',      label: 'Budget' },
        { key: 'timesheets',  label: 'Timesheets' },
        { key: 'costs',       label: 'Costs' },
        { key: 'billing',     label: 'Billing' },
        { key: 'revenue',     label: 'Revenue' },
      ],
    };
  },
  computed: {
    routePrefix() { return this.$route.params.company_code ? `/${this.$route.params.company_code}` : ''; },
    approvedBudgetTotal() { return this.budgets.find(b => b.status === 'approved')?.total_amount; },
    budgetLinesTotalAmt() { return this.budgetLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0); },
    totalCost() { return 0; },
    employeeOptions() { return this.employees.map(e => ({ value: e.id, name: `${e.first_name} ${e.last_name}` })); },
    memberEmployeeOptions() {
      return this.members
        .filter(m => m.employee)
        .map(m => ({ value: m.employee.id, name: `${m.employee.first_name} ${m.employee.last_name}` }));
    },
    wbsOptions() {
      const flatten = (nodes) => nodes.flatMap(n => [
        { value: n.id, name: `${n.code} – ${n.name}` },
        ...flatten(n.children ?? []),
      ]);
      return flatten(this.wbs);
    },
  },
  async created() { await this.fetchProject(); },
  methods: {
    async fetchProject() {
      try {
        const id = this.$route.params.id;
        const [proj, wbs, members, budgets, te, costs, billing, revenue] = await Promise.all([
          axios.get(`/projects/${id}`),
          axios.get(`/projects/${id}/wbs`),
          axios.get(`/projects/${id}/members`),
          axios.get(`/projects/${id}/budgets`),
          axios.get(`/projects/${id}/time-entries`),
          axios.get(`/projects/${id}/costs`),
          axios.get(`/projects/${id}/billing`),
          axios.get(`/projects/${id}/revenue`),
        ]);
        this.project     = proj.data.data;
        this.wbs         = wbs.data.data ?? [];
        this.members     = members.data.data ?? [];
        this.budgets     = budgets.data.data ?? [];
        this.timeEntries = te.data.data ?? [];
        this.costs       = costs.data.data ?? [];
        this.billing     = billing.data.data ?? [];
        this.revenue     = revenue.data.data ?? [];
      } catch { this.toast.error('Failed to load project.'); }
    },
    async activateProject() {
      try {
        await axios.post(`/projects/${this.project.id}/activate`);
        this.toast.success('Project activated.');
        this.fetchProject();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to activate.'); }
    },
    async confirmClose() {
      this.saving = true;
      try {
        await axios.post(`/projects/${this.project.id}/close`, this.closeForm);
        this.toast.success('Project closed.');
        this.closeModalOpen = false;
        this.fetchProject();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to close project.'); }
      finally { this.saving = false; }
    },
    async openAddMember() {
      this.memberForm = { employee_id: '', role: '', start_date: '', end_date: '' };
      if (!this.employees.length) {
        try {
          const { data } = await axios.get('/payroll/employees/active');
          this.employees = data.data ?? [];
        } catch { this.toast.error('Failed to load employees.'); return; }
      }
      this.addMemberOpen = true;
    },
    async saveMember() {
      if (!this.memberForm.employee_id) return this.toast.error('Please select an employee.');
      this.saving = true;
      try {
        await axios.post(`/projects/${this.project.id}/members`, this.memberForm);
        this.toast.success('Member added.');
        this.addMemberOpen = false;
        const res = await axios.get(`/projects/${this.project.id}/members`);
        this.members = res.data.data ?? [];
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to add member.');
      } finally { this.saving = false; }
    },
    showConfirm(title, desc, handler, btnLabel = 'Delete') {
      this.confirmModal = { open: true, title, desc, btnLabel };
      this._confirmFn = handler;
    },
    doConfirm() {
      this.confirmModal.open = false;
      if (this._confirmFn) this._confirmFn();
      this._confirmFn = null;
    },
    confirmRemoveMember(id) {
      this.showConfirm(
        'Remove team member?',
        'This will remove the member from the project. You can add them back at any time.',
        () => this.doRemoveMember(id),
        'Remove'
      );
    },
    async doRemoveMember(memberId) {
      try {
        await axios.delete(`/projects/${this.project.id}/members/${memberId}`);
        this.members = this.members.filter(m => m.id !== memberId);
        this.toast.success('Member removed.');
      } catch { this.toast.error('Failed to remove member.'); }
    },
    openAddWbs(parentId = null) {
      this.wbsEditing  = null;
      this.wbsParentId = parentId;
      this.wbsForm     = { code: '', name: '', type: 'task', planned_start: '', planned_end: '', planned_hours: '', billable: true, milestone_amount: '', milestone_due_date: '' };
      this.wbsModalOpen = true;
    },
    openEditWbs(node) {
      this.wbsEditing  = node.id;
      this.wbsParentId = node.parent_id;
      this.wbsForm = {
        code:                node.code               ?? '',
        name:                node.name               ?? '',
        type:                node.type               ?? 'task',
        planned_start:       node.planned_start       ? node.planned_start.slice(0, 10)       : '',
        planned_end:         node.planned_end         ? node.planned_end.slice(0, 10)         : '',
        planned_hours:       node.planned_hours       ?? '',
        billable:            node.billable            ?? true,
        milestone_amount:    node.milestone_amount    ?? '',
        milestone_due_date:  node.milestone_due_date  ? node.milestone_due_date.slice(0, 10)  : '',
      };
      this.wbsModalOpen = true;
    },
    async saveWbs() {
      if (!this.wbsForm.code || !this.wbsForm.name) {
        return this.toast.error('Code and name are required.');
      }
      this.saving = true;
      try {
        if (this.wbsEditing) {
          await axios.put(`/projects/wbs/${this.wbsEditing}`, this.wbsForm);
          this.toast.success('WBS element updated.');
        } else {
          await axios.post(`/projects/${this.project.id}/wbs`, {
            ...this.wbsForm,
            project_id: this.project.id,
            parent_id:  this.wbsParentId ?? null,
          });
          this.toast.success('WBS element added.');
        }
        this.wbsModalOpen = false;
        const { data } = await axios.get(`/projects/${this.project.id}/wbs`);
        this.wbs = data.data ?? [];
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to save WBS element.');
      } finally { this.saving = false; }
    },
    deleteWbs(id) {
      this.showConfirm(
        'Delete WBS element?',
        'This will permanently delete this element and all its children.',
        async () => {
          try {
            await axios.delete(`/projects/wbs/${id}`);
            this.toast.success('WBS element deleted.');
            const { data } = await axios.get(`/projects/${this.project.id}/wbs`);
            this.wbs = data.data ?? [];
          } catch (e) {
            this.toast.error(e.response?.data?.message ?? 'Failed to delete WBS element.');
          }
        }
      );
    },
    openLogTime() {
      this.logTimeForm = { employee_id: '', wbs_id: '', work_date: new Date().toISOString().slice(0, 10), hours: '', description: '', billable: true };
      this.logTimeOpen = true;
    },
    async saveTimeEntry() {
      if (!this.logTimeForm.employee_id || !this.logTimeForm.wbs_id || !this.logTimeForm.work_date || !this.logTimeForm.hours) {
        return this.toast.error('Employee, WBS element, date and hours are required.');
      }
      this.saving = true;
      try {
        const { data } = await axios.post(`/projects/${this.project.id}/time-entries`, this.logTimeForm);
        this.timeEntries.unshift(data.data);
        this.logTimeOpen = false;
        this.toast.success('Time entry logged.');
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to log time entry.');
      } finally { this.saving = false; }
    },
    deleteTimeEntry(id) {
      this.showConfirm(
        'Delete time entry?',
        'This will permanently remove this time entry from the project.',
        async () => {
          try {
            await axios.delete(`/projects/${this.project.id}/time-entries/${id}`);
            this.timeEntries = this.timeEntries.filter(e => e.id !== id);
            this.toast.success('Time entry deleted.');
          } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to delete time entry.'); }
        }
      );
    },
    openAddCost() {
      this.costForm = { wbs_id: '', source: 'expense', source_ref: '', cost_category: 'expenses', amount: '', date: new Date().toISOString().slice(0, 10), commitment: false, notes: '' };
      this.addCostOpen = true;
    },
    async saveCost() {
      if (!this.costForm.amount || !this.costForm.date) {
        return this.toast.error('Amount and date are required.');
      }
      this.saving = true;
      try {
        const { data } = await axios.post(`/projects/${this.project.id}/costs`, this.costForm);
        this.costs.unshift(data.data);
        this.addCostOpen = false;
        this.toast.success('Cost entry added.');
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to add cost entry.');
      } finally { this.saving = false; }
    },
    deleteCost(id) {
      this.showConfirm(
        'Delete cost entry?',
        'This will permanently remove this cost entry from the project.',
        async () => {
          try {
            await axios.delete(`/projects/${this.project.id}/costs/${id}`);
            this.costs = this.costs.filter(c => c.id !== id);
            this.toast.success('Cost entry deleted.');
          } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to delete cost entry.'); }
        }
      );
    },
    openAddBilling() {
      this.billingForm = { wbs_id: '', type: 'milestone', amount: '', due_date: '', notes: '' };
      this.addBillingOpen = true;
    },
    async saveBilling() {
      if (!this.billingForm.amount) return this.toast.error('Amount is required.');
      this.saving = true;
      try {
        const { data } = await axios.post(`/projects/${this.project.id}/billing`, this.billingForm);
        this.billing.unshift(data.data);
        this.addBillingOpen = false;
        this.toast.success('Billing event created.');
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to create billing event.');
      } finally { this.saving = false; }
    },
    async submitBilling(id) {
      try {
        const { data } = await axios.post(`/projects/billing/${id}/submit`);
        this.updateBillingRow(data.data);
        this.toast.success('Submitted for approval.');
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.'); }
    },
    async approveBilling(id) {
      try {
        const { data } = await axios.post(`/projects/billing/${id}/approve`);
        this.updateBillingRow(data.data);
        this.toast.success('Billing event approved.');
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.'); }
    },
    async rejectBilling(id) {
      const notes = prompt('Reason for rejection (optional):') ?? '';
      try {
        const { data } = await axios.post(`/projects/billing/${id}/reject`, { notes });
        this.updateBillingRow(data.data);
        this.toast.success('Billing event rejected.');
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.'); }
    },
    async markBillingInvoiced(id) {
      const ref = prompt('Enter invoice reference number:');
      if (ref === null) return;
      try {
        const { data } = await axios.post(`/projects/billing/${id}/invoiced`, { invoice_id: ref });
        this.updateBillingRow(data.data);
        this.toast.success('Marked as invoiced.');
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.'); }
    },
    deleteBilling(id) {
      this.showConfirm(
        'Delete billing event?',
        'This will permanently remove this billing event. Only draft and rejected events can be deleted.',
        async () => {
          try {
            await axios.delete(`/projects/billing/${id}`);
            this.billing = this.billing.filter(b => b.id !== id);
            this.toast.success('Billing event deleted.');
          } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.'); }
        }
      );
    },
    updateBillingRow(updated) {
      const idx = this.billing.findIndex(b => b.id === updated.id);
      if (idx >= 0) this.billing.splice(idx, 1, updated);
    },
    formatPeriod(p) {
      if (!p) return '—';
      const [y, m] = p.split('-');
      return new Date(+y, +m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    },
    async runRecognition() {
      if (!this.revenueForm.period) return this.toast.error('Please select a period.');
      this.saving = true;
      try {
        const { data } = await axios.post(`/projects/${this.project.id}/revenue/recognise`, this.revenueForm);
        this.revenue.unshift(data.data);
        this.runRevenueOpen = false;
        this.toast.success('Revenue recognised successfully.');
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to run revenue recognition.');
      } finally { this.saving = false; }
    },
    openNewBudget() {
      this.budgetForm  = { notes: '' };
      this.budgetLines = [];
      this.addBudgetOpen = true;
    },
    addBudgetLine() {
      this.budgetLines.push({ wbs_id: null, cost_category: 'expenses', amount: '', hours: '', notes: '' });
    },
    removeBudgetLine(i) {
      this.budgetLines.splice(i, 1);
    },
    async saveBudget() {
      if (!this.budgetLines.length) return this.toast.error('Add at least one line item.');
      this.saving = true;
      try {
        const { data } = await axios.post('/projects/budgets', {
          project_id: this.project.id,
          ...this.budgetForm,
          lines: this.budgetLines,
        });
        this.budgets.unshift(data.data);
        this.addBudgetOpen = false;
        this.toast.success('Budget created.');
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to create budget.');
      } finally { this.saving = false; }
    },
    async loadBudgetDetail(id) {
      if (this.activeBudget?.id === id) { this.activeBudget = null; return; }
      try {
        const { data } = await axios.get(`/projects/budgets/${id}`);
        this.activeBudget = data.data;
      } catch { this.toast.error('Failed to load budget details.'); }
    },
    async submitBudget(id) {
      try {
        const { data } = await axios.post(`/projects/budgets/${id}/submit`);
        this.updateBudgetRow(data.data);
        this.toast.success('Budget submitted for approval.');
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.'); }
    },
    async approveBudget(id) {
      try {
        const { data } = await axios.post(`/projects/budgets/${id}/approve`);
        this.updateBudgetRow(data.data);
        this.toast.success('Budget approved.');
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.'); }
    },
    async rejectBudget(id) {
      const notes = prompt('Reason for rejection (optional):') ?? '';
      try {
        const { data } = await axios.post(`/projects/budgets/${id}/reject`, { notes });
        this.updateBudgetRow(data.data);
        this.toast.success('Budget rejected.');
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.'); }
    },
    updateBudgetRow(updated) {
      const idx = this.budgets.findIndex(b => b.id === updated.id);
      if (idx >= 0) this.budgets.splice(idx, 1, updated);
      if (this.activeBudget?.id === updated.id) {
        this.activeBudget = { ...this.activeBudget, ...updated };
      }
    },
    budgetStatusLabel(s) {
      return { draft: 'Draft', pending_approval: 'Pending', approved: 'Approved', rejected: 'Rejected' }[s] ?? s ?? '—';
    },
    billingTypeLabel(t) {
      return { milestone: 'Milestone', fixed_fee: 'Fixed Fee', retainer: 'Retainer', time_and_material: 'T&M' }[t] ?? t ?? '—';
    },
    billingStatusLabel(s) {
      return { draft: 'Draft', pending_approval: 'Pending', approved: 'Approved', invoiced: 'Invoiced', rejected: 'Rejected' }[s] ?? s ?? '—';
    },
    sourceLabel(s) {
      return { time: 'Time', ap_invoice: 'AP Invoice', inventory: 'Inventory', expense: 'Expense', purchase_order: 'PO' }[s] ?? s ?? '—';
    },
    formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
    formatMoney(v) { return new Intl.NumberFormat('en-NG', { minimumFractionDigits: 2 }).format(v ?? 0); },
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────── */
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; align-items:center; gap:10px; }
.breadcrumb-sm   { display:flex; align-items:center; gap:6px; font-size:12px; color:#7a90b0; margin-bottom:4px; }
.breadcrumb-link { cursor:pointer; color:#004AAD; }
.breadcrumb-link:hover { text-decoration:underline; }
.breadcrumb-sep  { color:#c0cdd8; }
.mt-4  { margin-top:4px; }
.mt-16 { margin-top:16px; }

/* ── Summary cards ──────────────────────────────────────────────────── */
.summary-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:20px; }
.summary-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px 20px; }
.summary-label { font-size:11.5px; font-weight:600; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; margin-bottom:6px; }
.summary-value { font-size:20px; font-weight:700; color:#1a2b45; }

/* ── Tabs ───────────────────────────────────────────────────────────── */
.tab-bar { display:flex; gap:4px; border-bottom:2px solid #e4eaf2; margin-bottom:16px; }
.tab-btn { padding:10px 18px; border:none; background:none; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-2px; transition:color .15s; }
.tab-btn.active { color:#004AAD; border-bottom-color:#004AAD; }
.tab-btn:hover { color:#1a2b45; }

/* ── Card ───────────────────────────────────────────────────────────── */
.card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.card-header-row { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f4f8; }
.card-section-title { font-size:14px; font-weight:700; color:#1a2b45; }
.btn-sm-primary { background:#004AAD; color:#fff; border:none; padding:7px 14px; border-radius:7px; font-size:12.5px; font-weight:600; cursor:pointer; }
.btn-sm-primary:hover { background:#003b8e; }
.empty-state-sm { padding:32px; text-align:center; color:#7a90b0; font-size:13px; }
.loading-state  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; color:#7a90b0; gap:10px; }

/* ── Detail grid ────────────────────────────────────────────────────── */
.detail-grid  { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; padding:20px; }
.detail-item  { display:flex; flex-direction:column; gap:3px; }
.detail-label { font-size:11.5px; font-weight:600; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; }
.detail-value { font-size:13.5px; color:#1a2b45; }
.mono { font-family:monospace; font-size:12px; color:#004AAD; font-weight:600; }

/* ── Status badge ───────────────────────────────────────────────────── */
.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
.status-badge--draft     { background:#f1f5f9; color:#64748b; }
.status-badge--active    { background:#dcfce7; color:#15803d; }
.status-badge--on_hold   { background:#fff7ed; color:#c2410c; }
.status-badge--closed    { background:#f1f5f9; color:#475569; }
.status-badge--cancelled { background:#fee2e2; color:#dc2626; }
.status-badge--pending   { background:#eff6ff; color:#1d4ed8; }
.status-badge--approved  { background:#dcfce7; color:#15803d; }

/* ── Data table ─────────────────────────────────────────────────────── */
.data-table { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.data-table td { padding:13px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.amount-col { text-align:right; font-variant-numeric:tabular-nums; }
.data-table th.amount-col { text-align:right; }
.btn-link { background:none; border:none; font-size:12.5px; font-weight:600; color:#004AAD; cursor:pointer; padding:4px 6px; border-radius:5px; }
.btn-link:hover { background:#eff6ff; }
.btn-link--danger { color:#dc2626; }
.btn-link--danger:hover { background:#fee2e2; }

/* ── WBS tree ───────────────────────────────────────────────────────── */
.wbs-tree { padding:12px 16px; }
:deep(.wbs-item) { margin-bottom:4px; }
:deep(.wbs-row)  { display:flex; align-items:center; gap:10px; padding:8px 12px; border:1px solid #e4eaf2; border-radius:8px; background:#f8fafc; }
:deep(.wbs-code) { font-family:monospace; font-size:12px; color:#004AAD; font-weight:700; min-width:64px; }
:deep(.wbs-name) { flex:1; font-size:13px; color:#1a2b45; }
:deep(.wbs-actions) { display:flex; gap:4px; margin-left:auto; }
:deep(.wbs-children) { padding-left:24px; margin-top:4px; }
:deep(.btn-icon) { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; display:inline-flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; }
:deep(.btn-icon:hover) { background:#f4f7fb; }
:deep(.btn-icon--danger:hover) { background:#fee2e2; border-color:#fecaca; color:#dc2626; }

/* ── Buttons ────────────────────────────────────────────────────────── */
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.55; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.btn-outline--danger { border-color:#fecaca; color:#dc2626; }
.btn-outline--danger:hover { background:#fee2e2; }

/* ── Modal ──────────────────────────────────────────────────────────── */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:none; align-items:center; justify-content:center; padding:20px; }
.modal-overlay.open { display:flex; }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; }
.modal-close  { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; line-height:1; }
.modal-body   { padding:20px 24px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; }
.form-group   { margin-bottom:14px; }
.form-group label { display:block; font-size:12.5px; font-weight:600; color:#4a5568; margin-bottom:5px; }
.form-control { width:100%; padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-control:focus { border-color:#004AAD; }
.form-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.form-group--check { display:flex; align-items:flex-end; padding-bottom:2px; }
.checkbox-label { display:flex; align-items:center; gap:8px; font-size:13px; color:#1a2b45; cursor:pointer; }
.req { color:#e53e3e; }

/* ── Spinner ────────────────────────────────────────────────────────── */
.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

/* ── Multiselect overrides ──────────────────────────────────────────── */
.custom-multiselect { --ms-font-size:13px; --ms-line-height:1.5; --ms-min-height:40px; --ms-border-color:#dde5f0; --ms-border-radius:8px; --ms-ring-color:rgba(0,74,173,.2); --ms-dropdown-max-height:220px; --ms-option-font-size:13px; }
.text-dim { font-size:12px; color:#7a90b0; }
.notes-col { max-width:200px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:#7a90b0; font-size:12px; }
/* Revenue tab */
.method-badge { margin-left:10px; font-size:11.5px; font-weight:600; color:#004AAD; background:#eff6ff; padding:3px 10px; border-radius:20px; }
.rev-positive { color:#15803d; font-weight:600; }
.rev-info-box { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:14px 16px; font-size:13px; color:#1a2b45; line-height:1.6; }
.rev-info-desc { font-size:12px; }
.total-row td { background:#f8fafc; font-size:13px; border-top:2px solid #e4eaf2; }
.modal-box--sm { max-width:420px; }

/* Billing status badges */
.status-badge--bill-draft            { background:#f1f5f9; color:#475569; }
.status-badge--bill-pending_approval { background:#fef9c3; color:#854d0e; }
.status-badge--bill-approved         { background:#dcfce7; color:#15803d; }
.status-badge--bill-invoiced         { background:#eff6ff; color:#1d4ed8; }
.status-badge--bill-rejected         { background:#fee2e2; color:#dc2626; }
.type-badge { font-size:11.5px; font-weight:600; color:#7a90b0; }
.billing-actions { display:flex; gap:4px; flex-wrap:wrap; }
.btn-link--success { color:#15803d; }
.btn-link--success:hover { background:#dcfce7; }
/* Confirm modal */
.confirm-modal { padding:28px 24px 20px; text-align:center; }
.confirm-icon-wrap { margin-bottom:14px; }
.confirm-icon { font-size:36px; line-height:1; }
.confirm-body { margin-bottom:22px; }
.confirm-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.confirm-desc  { font-size:13px; color:#7a90b0; margin:0; line-height:1.5; }
.confirm-footer { display:flex; justify-content:center; gap:10px; }
.btn-danger { display:inline-flex; align-items:center; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; }
.btn-danger:hover { background:#b91c1c; }

/* Budget tab */
.modal-box--lg { max-width:820px; }
.row-selected td { background:#eff6ff; }
.budget-detail { border-top:2px solid #e4eaf2; }
.budget-detail-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; background:#f8fafc; }
.budget-detail-title { font-size:13.5px; font-weight:600; color:#1a2b45; display:flex; align-items:center; gap:8px; }
.budget-notes { padding:10px 18px; font-size:12.5px; color:#7a90b0; background:#fffbeb; border-bottom:1px solid #fde68a; }
.budget-lines-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
.budget-lines-table .form-control { padding:6px 8px; font-size:12.5px; }
.budget-lines-table .budget-line-ms { --ms-font-size:12.5px; --ms-min-height:34px; --ms-border-color:#dde5f0; --ms-border-radius:8px; }
.budget-total-preview { font-size:13px; font-weight:600; color:#1a2b45; margin-right:auto; }
.status-badge--bud-draft            { background:#f1f5f9; color:#475569; }
.status-badge--bud-pending_approval { background:#fef9c3; color:#854d0e; }
.status-badge--bud-approved         { background:#dcfce7; color:#15803d; }
.status-badge--bud-rejected         { background:#fee2e2; color:#dc2626; }

/* Cost category badges */
.status-badge--cat-labour      { background:#eff6ff; color:#1d4ed8; }
.status-badge--cat-materials   { background:#fef9c3; color:#854d0e; }
.status-badge--cat-subcontract { background:#fdf4ff; color:#7e22ce; }
.status-badge--cat-expenses    { background:#fff7ed; color:#c2410c; }
.status-badge--cat-overhead    { background:#f1f5f9; color:#475569; }

@media (max-width:768px) {
  .summary-grid { grid-template-columns:1fr 1fr; }
  .detail-grid  { grid-template-columns:1fr; }
}
</style>
